import { useEffect, useMemo } from 'react';

import { getCommunityFolderItem } from '@/services/community/communitiesLibraryService';

import {
  FILE_ITEM_STATUS_ERROR,
  STATUS_VIDEO_PROCESSING
} from '@/contexts/LibrarySetupContext/constants';

import {
  showErrorToast,
  showSuccessToast
} from '@/components/common/ToastContainer';

import { FOLDER_ITEM_TYPE_SECTION, STATUS_PUBLISHED } from '../constants';

const usePollProcessingFiles = ({
  communityId,
  folderItemsList,
  setFolderItemsList
}) => {
  // Get all folder items that have processing status.
  const publishingFolderItemsList = useMemo(() => {
    // Get folder items in root that are of status publishing
    let publishingFolderItemsList = [];

    folderItemsList?.forEach((folderItem) => {
      if (
        folderItem.type === FOLDER_ITEM_TYPE_SECTION &&
        Array.isArray(folderItem.items)
      ) {
        folderItem.items.forEach((sectionFolderItem) => {
          if (sectionFolderItem.status === STATUS_VIDEO_PROCESSING) {
            publishingFolderItemsList.push(sectionFolderItem);
          }
        });
      }

      if (folderItem.status === STATUS_VIDEO_PROCESSING) {
        publishingFolderItemsList.push(folderItem);
      }
    });

    return publishingFolderItemsList;
    // folderItemsList.filter(
    //   (folderItem) => folderItem.status === STATUS_VIDEO_PROCESSING
    // );
  }, [folderItemsList]);

  // Get folder item ids of all publishing folder items ids
  const currPublishingFolderItemIds = publishingFolderItemsList
    .map((folderItem) => folderItem._id)
    .filter(Boolean);

  // Needed in string format instead of array because passing array in useEffect dependency will lead to useEffect triggering even when data doesn't change but reference does.
  // Using string will ensure that useEffect runs only if value changes.
  const currPublishingFolderItemIdsStr = JSON.stringify(
    currPublishingFolderItemIds
  );

  /**
   * Polling logic.
   * Call get folder item API every 10s for each publishing folder item id.
   */
  useEffect(() => {
    if (!currPublishingFolderItemIds.length) {
      return;
    }

    const POLL_INTERVAL = 10000; // Poll every 10s

    const pollFn = async (currPublishingFolderItemIds) => {
      // Get latest folder item object for all folder items ids in currPublishingFolderItemIds
      const getFolderItemsPromises = currPublishingFolderItemIds.map(
        (folderItemId) =>
          getCommunityFolderItem({ communityId, folderItemId })
      );

      const folderItemResponses = await Promise.all(
        getFolderItemsPromises
      );

      const idToFolderItemMap = folderItemResponses.reduce(
        (acc, folderItemRes) => {
          const folderItemData = folderItemRes?.data?.data;
          if (folderItemData?._id) {
            acc[folderItemData._id] = folderItemData;
          }

          return acc;
        },
        {}
      );

      // Merge API res with setFolderItemsList
      setFolderItemsList((prevFolderItems) => {
        let update = [...prevFolderItems];
        Object.values(idToFolderItemMap).forEach((updatedFolderItem) => {
          // Check if updated folder Item is inside a section.
          const updatedFolderItemId = updatedFolderItem?._id;
          if (!updatedFolderItemId) return;
          const parentSectionId = updatedFolderItem?.parentSectionId;

          if (parentSectionId) {
            const sectionIndex = update.findIndex(
              (folderItem) => folderItem._id === parentSectionId
            );

            update[sectionIndex] = {
              ...update[sectionIndex],
              items: update[sectionIndex]?.items.map(
                (sectionFolderItem) => {
                  if (sectionFolderItem._id === updatedFolderItemId) {
                    return {
                      ...sectionFolderItem, // spread to keep the client id and other properties
                      ...updatedFolderItem
                    };
                  }
                  return sectionFolderItem;
                }
              )
            };
          }

          const updateFolderItemIdx = update.findIndex(
            (folderItem) => folderItem?._id === updatedFolderItemId
          );

          if (updateFolderItemIdx === -1) return;

          update[updateFolderItemIdx] = {
            ...update[updateFolderItemIdx], // spread to keep the client id and other properties
            ...updatedFolderItem
          };
        });

        return update;
      });

      // Show Toast
      folderItemResponses.forEach((folderItemRes) => {
        const folderItemData = folderItemRes?.data?.data;
        if (folderItemData) {
          const { title } = folderItemData;
          if (folderItemData.status === STATUS_PUBLISHED) {
            showSuccessToast(`${title} processing completed`);
          }
          if (folderItemData.status === FILE_ITEM_STATUS_ERROR) {
            showErrorToast(`Error processing ${title}`);
          }
        }
      });
    };

    // Start Poll
    pollFn(currPublishingFolderItemIds);

    // Poll every 10s
    const intervalId = setInterval(
      () => pollFn(currPublishingFolderItemIds),
      POLL_INTERVAL
    );

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currPublishingFolderItemIdsStr]);

  return {};
};

export default usePollProcessingFiles;
